import styles from "./Board.module.css";
import globalStyles from "../App.module.css";
import {
  GameState,
  IMove,
  IPiece,
  ISideEffect,
  Position,
} from "../services/zodiacEngine/types";
import { memo, useEffect, useState } from "react";
import Square from "./Square";

// Interface for movement destination highlight
interface MovementDestination {
  row: number;
  col: number;
}

interface BoardProps {
  gameState: GameState;
  uiState: {
    potentialMoves: IMove[];
    potentialSideEffects: ISideEffect[];
  };
  onDrop: (event: React.DragEvent<HTMLDivElement>, position: Position) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onSquareClick?: (position: Position) => void; // For mobile click-to-place
  // Highlighted rows for piece placement (e.g., home rows in setup)
  highlightedRows?: {
    light: number | null;
    dark: number | null;
  };
  // Tutorial information for special highlighting
  tutorialInfo?: {
    constellation: string;
    tutorialIndex: number;
    hasFirstClickHappened?: boolean;
    activeLossEffect?: {
      type: string;
      position: {row: number, col: number};
    } | null;
    suppressTutorialHighlights?: boolean;
  };
  // Control board orientation - true shows row 0 at bottom (light perspective), false shows row 7 at bottom (dark perspective)
  flipBoard?: boolean;
  // Callback for when a piece on the board starts being dragged (Sandbox/PuzzleDesigner)
  onPieceDragStart?: (event: React.DragEvent<HTMLImageElement>, piece: IPiece, position: Position) => void;
  // When true, board is in designation mode (PuzzleDesigner king/move selection) — suppresses piece selection, shows crosshair
  designateMode?: boolean;
  // When set, restricts piece selection/movement to only the specified side (used by Puzzles bestMove)
  puzzlePlayerIsLight?: boolean;
}

function Board(boardProps: BoardProps) {
  // Add state to track the hovered movement destination
  const [hoveredDestination, setHoveredDestination] = useState<MovementDestination | null>(null);

  useEffect(() => {
    // This effect will run whenever any prop value changes
    // console.log("Props changed:", boardProps); // DISABLED for debugging

    // Log mobile click handler status
    if (boardProps.onSquareClick) {
    } else {
     
    }
  }, [boardProps]);

  // function isSquarePotentialMove(position: Position): IMove | undefined {
  //   return boardProps?.uiState?.potentialMoves?.find(
  //     (x) => x.position.row === position.row && x.position.col === position.col
  //   );
  // }

  function getRowIndex(length: number, index: number): number {
    return length - 1 - index;
  }

  function getMovesForSquare(position: Position, potentialMoves: IMove[]) {
    return potentialMoves.filter(
      (move) =>
        move.position.row === position.row && move.position.col === position.col
    );
  }

  function getSideEffectsForSquare(
    position: Position,
    potentialSideEffects: ISideEffect[]
  ) {
    return potentialSideEffects.filter(
      (sideEffect) =>
        sideEffect.position.row === position.row &&
        sideEffect.position.col === position.col
    );
  }

  // Function to handle hover events from squares
  // const handleSquareHover = (move?: IMove) => {
  //   if (move && move.sideEffects) {
  //     // Collect positions from sideEffects to highlight
  //     const positions = move.sideEffects.map((se) => se.position);
  //     setHighlightedSquares(positions);
  //   } else {
  //     // Clear highlighted squares when not hovering over a move
  //     setHighlightedSquares([]);
  //   }
  // };

  // Determine if we should flip the board (default true for light player perspective)
  const shouldFlipBoard = boardProps.flipBoard !== undefined ? boardProps.flipBoard : true;

  // Prepare board array - reverse if showing light perspective (row 0 at bottom)
  const displayBoard = shouldFlipBoard
    ? [...boardProps.gameState.board].reverse()
    : boardProps.gameState.board;

  return (
    <div className={styles.board}>
      {displayBoard.map((row, rowIndex) => (
        <div key={rowIndex} className={globalStyles.boardrow}>
          {row.map((square, colIndex) => {
            const position = {
              row: shouldFlipBoard ? getRowIndex(boardProps.gameState.board.length, rowIndex) : rowIndex,
              col: colIndex,
            };

            //const move = isSquarePotentialMove(position);
            const moves = getMovesForSquare(
              position,
              boardProps.uiState.potentialMoves
            );
            const sideEffects = getSideEffectsForSquare(
              position,
              boardProps.uiState.potentialSideEffects
            );

            // Check if this square should be highlighted for piece placement
            const isHighlightedRow = boardProps.highlightedRows && (
              (boardProps.highlightedRows.light !== null && position.row === boardProps.highlightedRows.light) ||
              (boardProps.highlightedRows.dark !== null && position.row === boardProps.highlightedRows.dark)
            );

            return (
              <Square
                key={`${position.row}-${position.col}`}
                square={square}
                position={position}
                onSquareClick={boardProps.onSquareClick}
                onDrop={boardProps.onDrop}
                onDragOver={boardProps.onDragOver}
                //move={move}
                moves={moves}
                sideEffects={sideEffects}
                // Add props for movement destination highlight
                hoveredDestination={hoveredDestination}
                setHoveredDestination={setHoveredDestination}
                // Pass tutorial information for special highlighting
                tutorialInfo={boardProps.tutorialInfo}
                // Pass highlighted row information
                isHomeRowHighlight={isHighlightedRow}
                onPieceDragStart={boardProps.onPieceDragStart}
                designateMode={boardProps.designateMode}
                puzzlePlayerIsLight={boardProps.puzzlePlayerIsLight}
                //highlightedSquares={highlightedSquares}
                //onHover={handleSquareHover}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default memo(Board);
